/* ==========================================================================
   a11y.js — Global accessibility scripts
   Loaded (deferred) on every page from layout/theme.liquid
   ========================================================================== */
(function () {
  'use strict';

  var html = document.documentElement;

  // Live region for screen-reader announcements: window.a11yAnnounce('Added to cart')
  var liveRegion = document.createElement('div');
  liveRegion.className = 'a11y-sr-only';
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  document.body.appendChild(liveRegion);

  window.a11yAnnounce = function (message) {
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = message; }, 100);
  };

  /* ------------------------------------------------------------------------
     Skip link (#skip): move keyboard/screen-reader focus into <main>,
     not just scroll to it
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('#skip a[href^="#"]');
    if (!link) { return; }
    var target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) { return; }
    e.preventDefault();
    if (!target.hasAttribute('tabindex')) { target.setAttribute('tabindex', '-1'); }
    target.focus();
    target.scrollIntoView();
  });

  /* ------------------------------------------------------------------------
     Back to top (#totop, snippets/back-to-top-button.liquid): the browser
     scrolls to #root but keyboard focus stays at the bottom of the page.
     Send focus to the top (#skip, which holds the skip link) too.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('#totop a')) { return; }
    setTimeout(function () {
      var skip = document.getElementById('skip');
      if (!skip) { return; }
      if (!skip.hasAttribute('tabindex')) { skip.setAttribute('tabindex', '-1'); }
      skip.focus({ preventScroll: true });
    }, 0);
  });

  /* ------------------------------------------------------------------------
     Header menu (#nav): desktop mega menu + mobile drawer
     The theme (custom-async.js) builds and opens/closes the menu. This only
     layers ARIA state, keyboard support and focus management on top of it.
     ------------------------------------------------------------------------ */
  var MOBILE = window.matchMedia('(max-width: 1000px)');
  var MENU_STRINGS = {
    submenu: 'submenu',
    back: 'Back to main menu',
    close: 'Close menu'
  };
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var submenuId = 0;
  var drawerWasOpen = false;
  var drawerOpener = null;
  var hiddenByDrawer = [];

  function getNav() {
    return document.getElementById('nav');
  }

  function isShown(el) {
    return !!el && el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== 'hidden';
  }

  function isDrawerOpen() {
    return MOBILE.matches && html.classList.contains('m2a');
  }

  function cleanText(el) {
    return el ? (el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function topItems(nav) {
    return nav.querySelectorAll(':scope > ul:not(.inner) > li');
  }

  function mainToggle(li) {
    return li.querySelector(':scope > a.toggle:not(.toggle-back)');
  }

  function backToggle(li) {
    return li.querySelector(':scope > a.toggle-back');
  }

  function menuButtons() {
    return document.querySelectorAll('.shopify-section-header .link-btn a[aria-controls="nav"]');
  }

  function syncExpanded() {
    var nav = getNav();
    if (!nav) { return; }
    topItems(nav).forEach(function (li) {
      var toggle = mainToggle(li);
      if (toggle) {
        toggle.setAttribute('aria-expanded', li.classList.contains('toggle') ? 'true' : 'false');
      }
    });
    var open = html.classList.contains('m2a') ? 'true' : 'false';
    menuButtons().forEach(function (btn) {
      btn.setAttribute('aria-expanded', open);
    });
    syncDrawerDialog(nav);
  }

  // Mobile: the open menu is a modal drawer (focus is already trapped by the
  // theme). Say so while it is open, and move focus into it when it opens.
  var dialogWasOpen = false;
  function syncDrawerDialog(nav) {
    var open = isDrawerOpen();
    if (open) {
      nav.setAttribute('role', 'dialog');
      nav.setAttribute('aria-modal', 'true');
    } else {
      nav.removeAttribute('role');
      nav.removeAttribute('aria-modal');
    }
    if (open && !dialogWasOpen) {
      setTimeout(function () {
        if (!isDrawerOpen() || nav.contains(document.activeElement)) { return; }
        var first = Array.prototype.find.call(nav.querySelectorAll('a[href], button, input, [tabindex="0"]'), isShown);
        if (first) { first.focus(); }
      }, 400);
    }
    dialogWasOpen = open;
  }

  // Same steps as the theme's own close branch, without its hover side effects
  function closeSubmenu(li) {
    var nav = getNav();
    li.classList.remove('toggle', 'mtoggle');
    li.parentElement.classList.remove('ul-toggle');
    if (nav && !nav.querySelector('.ul-toggle')) {
      nav.removeAttribute('data-type');
    }
    html.classList.remove('nav-hover');
    syncExpanded();
  }

  function enhanceMenu() {
    var nav = getNav();
    if (!nav) { return; }

    // Mobile menu button ("View catalog" / burger)
    menuButtons().forEach(function (btn) {
      btn.setAttribute('role', 'button');
      // Shown only on mobile, as an icon (its "View catalog" text is hidden); it opens "Menu"
      btn.setAttribute('aria-label', 'Menu');
      if (btn.dataset.a11yBound) { return; }
      btn.dataset.a11yBound = 'true';
      btn.addEventListener('click', function () { drawerOpener = btn; });
    });

    topItems(nav).forEach(function (li) {
      var toggle = mainToggle(li);
      var back = backToggle(li);
      var link = li.querySelector(':scope > a:not(.toggle)');
      var sub = li.querySelector(':scope > ul, :scope > form');
      var title = cleanText(link);

      // Current page: expose on the link, not only the <li>
      if (link && li.getAttribute('aria-current') === 'page') {
        link.setAttribute('aria-current', 'page');
      }

      if (toggle) {
        toggle.setAttribute('role', 'button');
        if (title) { toggle.setAttribute('aria-label', title + ' ' + MENU_STRINGS.submenu); }
        if (sub) {
          if (!sub.id) { sub.id = 'a11y-submenu-' + (++submenuId); }
          toggle.setAttribute('aria-controls', sub.id);
        }
        if (!toggle.dataset.a11yBound) {
          toggle.dataset.a11yBound = 'true';
          toggle.addEventListener('click', function () {
            setTimeout(function () {
              syncExpanded();
              // Mobile drill-down: move focus into the opened panel
              if (isDrawerOpen() && li.classList.contains('toggle')) {
                var target = backToggle(li);
                if (!isShown(target)) {
                  target = Array.prototype.find.call(li.querySelectorAll(':scope > ul a[href]'), isShown);
                }
                if (target) { target.focus(); }
              }
            }, 50);
          });
        }
      }

      if (back) {
        back.setAttribute('role', 'button');
        back.setAttribute('aria-label', MENU_STRINGS.back);
        back.removeAttribute('aria-controls');
        back.removeAttribute('aria-expanded');
        if (!back.dataset.a11yBound) {
          back.dataset.a11yBound = 'true';
          back.addEventListener('click', function () {
            setTimeout(function () {
              syncExpanded();
              var t = mainToggle(li);
              if (isDrawerOpen() && !li.classList.contains('toggle') && isShown(t)) { t.focus(); }
            }, 50);
          });
        }
      }
    });

    // Drawer close buttons
    document.querySelectorAll('.shopify-section-header a.close-nav').forEach(function (el) {
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', MENU_STRINGS.close);
    });

    // Image-only links in the mega menu (promo collections) need a name
    nav.querySelectorAll('a[href]:not([aria-label])').forEach(function (a) {
      if (cleanText(a)) { return; }
      var img = a.parentElement && a.parentElement.querySelector('img[alt]');
      if (img && img.alt.trim()) { a.setAttribute('aria-label', img.alt.trim()); }
    });

    syncExpanded();
  }

  // While the mobile drawer is open, hide the rest of the page from screen readers
  function hideOutsideDrawer(nav) {
    var node = nav;
    while (node && node.parentElement && node !== document.body) {
      Array.prototype.forEach.call(node.parentElement.children, function (sib) {
        if (sib === node || sib === liveRegion || /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(sib.tagName) ||
            sib.hasAttribute('aria-hidden') || sib.classList.contains('close-nav') || sib.classList.contains('overlay-close')) {
          return;
        }
        sib.setAttribute('aria-hidden', 'true');
        hiddenByDrawer.push(sib);
      });
      node = node.parentElement;
    }
  }

  function restoreOutsideDrawer() {
    hiddenByDrawer.forEach(function (el) { el.removeAttribute('aria-hidden'); });
    hiddenByDrawer = [];
  }

  function onDrawerChange() {
    var nav = getNav();
    var open = isDrawerOpen();
    syncExpanded();
    if (!nav || open === drawerWasOpen) { return; }
    drawerWasOpen = open;

    if (open) {
      if (!drawerOpener) { drawerOpener = document.activeElement; }
      hideOutsideDrawer(nav);
      return;
    }

    restoreOutsideDrawer();
    var active = document.activeElement;
    if (!active || active === document.body || nav.contains(active)) {
      var target = isShown(drawerOpener) ? drawerOpener : Array.prototype.find.call(menuButtons(), isShown);
      if (target) { target.focus(); }
    }
    drawerOpener = null;
  }

  function closeDrawer(nav) {
    var close = Array.prototype.find.call(nav.querySelectorAll('a.close-nav'), isShown) ||
                document.querySelector('.shopify-section-header a.close-nav');
    if (close) { close.click(); }
  }

  function trapFocus(e, nav) {
    var items = Array.prototype.filter.call(nav.querySelectorAll(FOCUSABLE), isShown);
    if (!items.length) { return; }
    var first = items[0];
    var last = items[items.length - 1];
    if (!nav.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('keydown', function (e) {
    var nav = getNav();
    if (!nav) { return; }

    if (e.key === 'Tab' && isDrawerOpen()) {
      trapFocus(e, nav);
      return;
    }
    if (e.key !== 'Escape' && e.key !== 'Esc') { return; }

    if (isDrawerOpen()) {
      // Step back one level first, then close the drawer
      var openLi = nav.querySelector(':scope > ul:not(.inner) > li.toggle');
      var back = openLi && backToggle(openLi);
      if (isShown(back)) {
        back.click();
      } else {
        closeDrawer(nav);
      }
      e.preventDefault();
      return;
    }

    var li = document.activeElement && document.activeElement.closest('#nav > ul > li.toggle');
    if (li) {
      closeSubmenu(li);
      var toggle = mainToggle(li);
      if (toggle) { toggle.focus(); }
      e.preventDefault();
    }
  });

  function initMenu() {
    var nav = getNav();
    if (!nav) { return; }
    enhanceMenu();

    // Desktop: close a keyboard-opened submenu once focus leaves it
    nav.addEventListener('focusout', function (e) {
      if (MOBILE.matches) { return; }
      var li = e.target.closest('#nav > ul > li.toggle');
      if (li && !li.contains(e.relatedTarget)) { closeSubmenu(li); }
    });

    // The theme builds and updates the menu asynchronously, so re-apply on change
    var queued = false;
    new MutationObserver(function () {
      if (queued) { return; }
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        enhanceMenu();
      });
    }).observe(nav, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    new MutationObserver(onDrawerChange).observe(html, { attributes: true, attributeFilter: ['class'] });
    if (MOBILE.addEventListener) {
      MOBILE.addEventListener('change', onDrawerChange);
    }
  }

  initMenu();

  /* ------------------------------------------------------------------------
     Desktop menu: no hover-open while a screen reader / keyboard moves focus
     VoiceOver and NVDA can drag the mouse pointer along with their cursor, and
     that pointer opened submenus on hover. While focus is moved without the
     mouse, hover is paused (html.a11y-hover-paused, see a11y.css); submenus
     then open only with their toggle button. Real mouse movement resumes it.
     ------------------------------------------------------------------------ */
  var HOVER_PAUSED = 'a11y-hover-paused';
  var hoverPaused = false;
  var lastPointerDown = 0;
  var moveCount = 0;
  var lastMoveTime = 0;

  function pauseHover() {
    if (hoverPaused || MOBILE.matches) { return; }
    hoverPaused = true;
    moveCount = 0;
    html.classList.add(HOVER_PAUSED);
    // Undo the theme's hover overlay unless a submenu was opened with its toggle
    var nav = getNav();
    if (nav && !nav.querySelector(':scope > ul > li.toggle')) {
      html.classList.remove('nav-hover');
      nav.classList.remove('ul-hover');
    }
  }

  function resumeHover(e) {
    hoverPaused = false;
    html.classList.remove(HOVER_PAUSED);
    // Let the theme's hover script see the item the pointer is already on
    var el = document.elementFromPoint(e.clientX, e.clientY);
    if (el && el.closest('#nav')) {
      el.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, view: window, clientX: e.clientX, clientY: e.clientY }));
    }
  }

  window.addEventListener('pointerdown', function () { lastPointerDown = Date.now(); }, true);
  window.addEventListener('keydown', pauseHover, true);
  window.addEventListener('focusin', function () {
    if (Date.now() - lastPointerDown > 1000) { pauseHover(); }
  }, true);

  // A pointer moved by a screen reader jumps once; a real mouse sends a stream of moves
  window.addEventListener('mousemove', function (e) {
    if (!hoverPaused || !(e.movementX || e.movementY)) { return; }
    moveCount = e.timeStamp - lastMoveTime < 100 ? moveCount + 1 : 1;
    lastMoveTime = e.timeStamp;
    if (moveCount >= 4) { resumeHover(e); }
  }, true);

  // Keep the theme's hover script (navSubHover, nav-hover overlay) from running meanwhile
  window.addEventListener('mouseover', function (e) {
    if (hoverPaused && e.isTrusted && e.target.closest && e.target.closest('#nav')) { e.stopPropagation(); }
  }, true);

  /* ------------------------------------------------------------------------
     Product card size picker (ul.variant-picker, snippets/product-item.liquid)
     Picking a size adds it to the cart straight away (custom-async.js), so
     arrow keys must only move focus, not select. Space/Enter/click select.
     ------------------------------------------------------------------------ */
  var ARROWS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

  document.addEventListener('keydown', function (e) {
    var radio = e.target;
    if (!radio.matches || !radio.matches('ul.variant-picker input[type="radio"]')) { return; }
    var list = radio.closest('ul.variant-picker');
    var radios = Array.prototype.filter.call(list.querySelectorAll('input[type="radio"]'), function (r) {
      return r.parentElement.style.display !== 'none';
    });
    var i = radios.indexOf(radio);

    if (e.key in ARROWS || e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      var next = e.key === 'Home' ? 0 : e.key === 'End' ? radios.length - 1 : (i + ARROWS[e.key] + radios.length) % radios.length;
      if (radios[next]) { radios[next].focus(); }
    } else if (e.key === 'Enter' || ((e.key === ' ' || e.key === 'Spacebar') && radio.checked)) {
      // Enter, or Space on an already-checked radio, still adds the size
      e.preventDefault();
      radio.checked = false;
      radio.click();
    }
  }, true);

  // Sold-out sizes are only greyed out (label.disabled-style): say so
  function syncSoldOut(label) {
    var input = label.previousElementSibling;
    var note = label.querySelector('.a11y-sold-out');
    var soldOut = label.classList.contains('disabled-style');
    if (soldOut && !note) {
      note = document.createElement('span');
      note.className = 'a11y-sr-only a11y-sold-out';
      note.textContent = ', sold out';
      label.appendChild(note);
    } else if (!soldOut && note) {
      note.remove();
    }
    if (input && input.type === 'radio') {
      if (soldOut) { input.setAttribute('aria-disabled', 'true'); } else { input.removeAttribute('aria-disabled'); }
    }
  }

  function enhanceVariantPickers(root) {
    (root || document).querySelectorAll('ul.variant-picker label').forEach(syncSoldOut);
  }

  /* ------------------------------------------------------------------------
     Auto-rotating carousels: homepage slideshow (.m6fr) and announcement bar
     Swiper is set up in custom.js; its instance lives on .swiper-outer.
     - Pause while keyboard focus is inside, resume when it leaves
     - Stay paused for prefers-reduced-motion
     - Slideshow: theme's Play/Pause link becomes a real toggle button
     - Announcement bar: has no pause control, so add one (shown on focus)
     - Slides: "slide, 1 of 3"; slides out of view are hidden from screen
       readers and taken out of the tab order (inert) until they slide in
     - Dots: real buttons named "Go to slide N"
     The structure is added whether or not the carousel rotates; the pause
     handling only when autoplay is on in the theme settings.
     ------------------------------------------------------------------------ */
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var CAROUSELS = [
    { selector: '.m6fr', label: 'Featured promotions', thing: 'slideshow' },
    { selector: '.shopify-section-announcement-bar', label: 'Announcements', thing: 'announcements' },
    // Product page gallery slider (mobile/tablet copy, desktop shows a static list)
    { selector: '#main-product .l4pr.s4wi', label: 'Product images', thing: 'images', bullet: 'Show image' }
  ];

  function syncSlides(root, swiper, cfg) {
    var outer = swiper.el.getBoundingClientRect();
    var total = 0;
    swiper.slides.forEach(function (slide) {
      var n = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
      if (!isNaN(n) && n + 1 > total) { total = n + 1; }
    });
    if (!total) { total = swiper.slides.length; }

    swiper.slides.forEach(function (slide, i) {
      var n = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', (isNaN(n) ? i + 1 : n + 1) + ' of ' + total);

      // Only slides fully outside the frame: anything you can see stays usable
      var r = slide.getBoundingClientRect();
      var shown = r.right > outer.left + 1 && r.left < outer.right - 1 && r.width > 0;
      if (shown) {
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.inert = true;
      }
    });

    root.querySelectorAll('.swiper-pagination-bullet').forEach(function (bullet, i, all) {
      bullet.setAttribute('role', 'button');
      bullet.setAttribute('aria-label', (cfg.bullet || 'Go to slide') + ' ' + (i + 1) + ' of ' + all.length);
      // Gallery thumbnails: the button name says it all ("Thumbnail" alt otherwise)
      bullet.querySelectorAll('img').forEach(function (img) { img.setAttribute('alt', ''); });
      if (bullet.classList.contains('swiper-pagination-bullet-active')) {
        bullet.setAttribute('aria-current', 'true');
      } else {
        bullet.removeAttribute('aria-current');
      }
    });
  }

  function setupCarousel(root, cfg) {
    if (root.dataset.a11yCarousel) { return true; }
    var outer = root.querySelector('.swiper-outer');
    var swiper = outer && outer.swiper;
    if (!swiper) { return false; }
    root.dataset.a11yCarousel = 'true';

    // "region" isn't allowed on a list (the product gallery is a <ul>)
    root.setAttribute('role', /^(UL|OL)$/.test(root.tagName) ? 'group' : 'region');
    root.setAttribute('aria-roledescription', 'carousel');
    root.setAttribute('aria-label', cfg.label);

    function sync() { syncSlides(root, swiper, cfg); }
    sync();
    swiper.on('slideChangeTransitionEnd', sync);
    swiper.on('transitionEnd', sync);
    swiper.on('resize', sync);
    swiper.on('paginationUpdate', sync);
    // Enter/Space on arrows and dots: Swiper only listens inside its own
    // element, and the theme puts these controls outside it
    root.addEventListener('keydown', function (e) {
      var control = e.target.closest && e.target.closest('.swiper-pagination-bullet, .swiper-button-nav');
      if (!control || swiper.el.contains(control) || (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar')) { return; }
      // Gallery thumbnails are <a href>: Enter already clicks them
      if (e.key === 'Enter' && control.matches('a[href]')) { return; }
      e.preventDefault();
      control.click();
    });

    if (!swiper.params.autoplay || !swiper.params.autoplay.enabled) { return true; }

    var userPaused = false;
    var toggle = root.querySelector('.play-pause');
    if (!toggle) {
      toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'a11y-carousel-pause';
      // Inside .swiper-outer: async-announcement.css hides "bar > * ~ *"
      outer.insertBefore(toggle, outer.firstChild);
    }
    toggle.setAttribute('role', 'button');

    function isPaused() {
      return userPaused || root.classList.contains('paused');
    }
    function syncToggle() {
      var paused = isPaused();
      toggle.setAttribute('aria-label', (paused ? 'Play ' : 'Pause ') + cfg.thing);
      if (toggle.tagName === 'BUTTON') { toggle.textContent = paused ? 'Play' : 'Pause'; }
    }

    if (toggle.tagName === 'BUTTON') {
      toggle.addEventListener('click', function () {
        userPaused = !userPaused;
        if (userPaused) { swiper.autoplay.stop(); } else { swiper.autoplay.start(); }
        syncToggle();
      });
    } else {
      // Theme's link toggles .paused itself on click; keep the label in step
      toggle.addEventListener('click', function () { setTimeout(syncToggle, 0); });
      toggle.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); toggle.click(); }
      });
    }

    function pauseForMotion() {
      if (!REDUCED_MOTION.matches) { return; }
      userPaused = true;
      root.classList.add('paused');
      swiper.autoplay.stop();
      syncToggle();
    }
    pauseForMotion();
    if (REDUCED_MOTION.addEventListener) { REDUCED_MOTION.addEventListener('change', pauseForMotion); }

    root.addEventListener('focusin', function () { swiper.autoplay.stop(); });
    root.addEventListener('focusout', function (e) {
      if (root.contains(e.relatedTarget) || isPaused()) { return; }
      swiper.autoplay.start();
    });
    // The theme restarts autoplay on mouseleave; don't let that override a user pause
    root.addEventListener('mouseleave', function () {
      if (userPaused) { setTimeout(function () { swiper.autoplay.stop(); }, 0); }
    });

    syncToggle();
    return true;
  }

  function enhanceCarousels() {
    var pending = false;
    CAROUSELS.forEach(function (cfg) {
      document.querySelectorAll(cfg.selector).forEach(function (root) {
        // A single slide has no slider: nothing to do
        if (!root.querySelector('.swiper-outer') && root.children.length < 2) { return; }
        if (!setupCarousel(root, cfg)) { pending = true; }
      });
    });
    return pending;
  }

  // Sliders are built after load; retry for a while until Swiper is ready
  var carouselTries = 0;
  (function waitForCarousels() {
    if (enhanceCarousels() || (carouselTries++ < 20 && !document.querySelector('.s4wi'))) {
      if (carouselTries < 40) { setTimeout(waitForCarousels, 500); }
    }
  })();

  /* ------------------------------------------------------------------------
     Links whose text alone doesn't say where they go ("Shop now", "View all",
     "Read more", ...): aria-label = the visible text + where it leads (the
     nearby heading, or else the destination). The visible text comes first,
     so speech-input users can still say what they see.
     ------------------------------------------------------------------------ */
  var VAGUE_LINK = /^(shop|shop now|shop all|discover|discover more|explore|learn more|read more|read more here|more|more info|view|view all|view more|view all results|see all|see more|show all|click here|here|go|details)\b[\s>›»:]*$/i;

  function visibleText(a) {
    var text = '';
    a.childNodes.forEach(function (n) {
      if (n.nodeType === 3) { text += n.nodeValue; }
      else if (n.nodeType === 1 && !n.classList.contains('a11y-sr-only') && n.getAttribute('aria-hidden') !== 'true') { text += n.textContent; }
    });
    return text.replace(/\s+/g, ' ').trim();
  }

  function linkDestination(a) {
    var url;
    try { url = new URL(a.getAttribute('href'), location.href); } catch (e) { return ''; }
    var m = /^\/(collections|products|pages|blogs)\/([^/?#]+)/.exec(url.pathname);
    if (url.pathname === '/search') { return 'search results' + (url.searchParams.get('q') ? ' for ' + url.searchParams.get('q') : ''); }
    if (m) {
      var name = decodeURIComponent(m[2]).replace(/-/g, ' ');
      return name + (m[1] === 'collections' ? ' collection' : '');
    }
    return '';
  }

  function linkContext(a) {
    // Search links say what they search for; menus and search results have
    // product names nearby that are not what the link is about
    if (/^\/search\b/.test(a.getAttribute('href') || '') || a.closest('#livesearch, #nav, #nav-top, #nav-bar')) {
      return linkDestination(a);
    }
    // Nearest heading in the same card / slide / section
    var box = a.closest('article, li, .shopify-section, header');
    var heading = box && box.querySelector('h1, h2, h3, h4, h5, h6');
    var text = cleanText(heading).replace(/\s*Close$/, '');
    return text || linkDestination(a);
  }

  function enhanceGenericLinks() {
    // Links that already have a name (menus, cards, ...) keep it
    document.querySelectorAll('a[href]:not([data-a11y-context]):not([aria-label])').forEach(function (a) {
      var text = visibleText(a);
      if (!text || !VAGUE_LINK.test(text)) { return; }
      a.dataset.a11yContext = 'true';
      var context = linkContext(a);
      if (!context || context.toLowerCase() === text.toLowerCase()) { return; }
      // Replace the hidden-text version used before 2026-10-18
      a.querySelectorAll(':scope > .a11y-sr-only').forEach(function (s) { s.remove(); });
      a.setAttribute('aria-label', text.replace(/[\s>›»:]+$/, '') + ': ' + context);
    });
  }

  /* ------------------------------------------------------------------------
     Links that open a new tab, email and phone links: say so
     ------------------------------------------------------------------------ */
  var NEW_TAB_NOTE = ' (opens in a new tab)';

  function enhanceSpecialLinks() {
    document.querySelectorAll('a[href^="mailto:"]:not([data-a11y-special]), a[href^="tel:"]:not([data-a11y-special])').forEach(function (a) {
      a.dataset.a11ySpecial = 'true';
      var isMail = /^mailto:/i.test(a.getAttribute('href'));
      var text = visibleText(a) || decodeURIComponent(a.getAttribute('href').replace(/^(mailto|tel):/i, '').split('?')[0]);
      a.setAttribute('aria-label', (isMail ? 'Email Address: ' : 'Phone Number: ') + text);
    });

    // Links to other websites open in a new tab (UPS tracking, app content, ...).
    // Not: this store's own domain, its Shopify customer account / checkout,
    // Shop Pay, popups (fancybox) and panels.
    var base = location.hostname.replace(/^www\./, '');
    document.querySelectorAll('a[href]:not([data-a11y-external]):not([data-fancybox]):not([data-panel])').forEach(function (a) {
      var url;
      try { url = new URL(a.getAttribute('href'), location.href); } catch (e) { return; }
      if (!/^https?:$/.test(url.protocol)) { return; }
      var host = url.hostname.replace(/^www\./, '');
      if (host === base || host.slice(-(base.length + 1)) === '.' + base) { return; }
      if (/(^|\.)myshopify\.com$|(^|\.)shop\.app$/.test(host)) { return; }
      if (/(^|\.)shopify\.com$/.test(host) && /\/(account|checkouts?|authentication|customer_authentication)\b|^\/\d+\//.test(url.pathname)) { return; }
      a.dataset.a11yExternal = 'true';
      if (a.target && a.target !== '_blank' && a.target !== 'external') { return; }
      a.target = '_blank';
      var rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      ['noopener', 'noreferrer'].forEach(function (r) { if (rel.indexOf(r) === -1) { rel.push(r); } });
      a.setAttribute('rel', rel.join(' '));
    });

    document.querySelectorAll('a[target="_blank"]:not([data-a11y-newtab])').forEach(function (a) {
      a.dataset.a11yNewtab = 'true';
      var label = a.getAttribute('aria-label');
      var img = a.querySelector('img[alt]:not([alt=""])');
      var name = label || cleanText(a) || (img ? img.alt.trim() : '');
      if (!name || /new (tab|window)/i.test(name)) { return; }
      if (label || !visibleText(a)) {
        // Icon-only links: keep their name (alt/title) and add the note
        a.setAttribute('aria-label', name + NEW_TAB_NOTE);
      } else {
        var span = document.createElement('span');
        span.className = 'a11y-sr-only';
        span.textContent = NEW_TAB_NOTE;
        a.appendChild(span);
      }
    });
  }

  enhanceGenericLinks();
  enhanceSpecialLinks();
  window.addEventListener('load', function () {
    enhanceGenericLinks();
    enhanceSpecialLinks();
  });

  /* ------------------------------------------------------------------------
     Header cart link: say how many items are in the cart
     (#cart-count sits inside an aria-hidden icon, and aria-label overrides it)
     ------------------------------------------------------------------------ */
  // Add to cart (product page button, card button or card size) gives no
  // spoken feedback: remember which product, announce when the count goes up
  var lastCartCount = null;
  var lastAddedName = '';
  var lastAddedAt = 0;

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('form[action*="/cart/add"] [type="submit"], ul.variant-picker input, ul.variant-picker label, a.overlay-tertiary');
    if (!t) { return; }
    // A card size pick makes the theme click the card's submit button: keep the size
    if (!e.isTrusted && Date.now() - lastAddedAt < 1500) { return; }
    lastAddedAt = Date.now();
    // .product-card first: sizes sit in their own <li> inside the card
    var box = t.closest('.product-card') || t.closest('#main-product') || t.closest('article, li');
    var title = box && box.querySelector('h1, h2, h3');
    lastAddedName = cleanText(title);
    var size = t.closest('ul.variant-picker') && (t.tagName === 'LABEL' ? t : t.nextElementSibling);
    if (size && cleanText(size)) { lastAddedName += ', size ' + cleanText(size).replace(/\s*,\s*sold out$/, ''); }
  }, true);

  function syncCartLabel() {
    var count = document.getElementById('cart-count');
    var link = count && count.closest('a');
    if (!link) { return; }
    var n = parseInt(count.textContent, 10) || 0;
    var label = 'Cart, ' + n + (n === 1 ? ' item' : ' items');
    link.setAttribute('aria-label', label);
    if (lastCartCount !== null && n > lastCartCount && lastAddedName) {
      window.a11yAnnounce('Added to cart: ' + lastAddedName + '. ' + label + '.');
      lastAddedName = '';
    } else if (lastCartCount !== null && n !== lastCartCount) {
      window.a11yAnnounce('Cart updated. ' + label + '.');
    }
    lastCartCount = n;
  }
  syncCartLabel();
  var cartCount = document.getElementById('cart-count');
  if (cartCount) {
    new MutationObserver(syncCartLabel).observe(cartCount, { childList: true, characterData: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Side panels (.m6pn: cart drawer, quick shop, filters, mobile login)
     Theme (custom-async.js openPanel/hidePanels) opens/closes them and
     toggles aria-hidden. Add dialog semantics, a focus trap, and always
     return focus to the opener.
     ------------------------------------------------------------------------ */
  var panelOpener = null;
  var openPanelEl = null;
  var PANEL_NAMES = { cart: 'Cart', 'filters-aside': 'Filters', quickshop: 'Quick shop', login: 'Log in' };

  function enhancePanel(panel) {
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    if (panel.hasAttribute('aria-labelledby') || panel.hasAttribute('aria-label')) { return; }
    var heading = panel.querySelector('h1, h2, h3');
    if (heading && cleanText(heading)) {
      if (!heading.id) { heading.id = 'a11y-panel-title-' + (panel.id || Math.random().toString(36).slice(2)); }
      panel.setAttribute('aria-labelledby', heading.id);
    } else {
      panel.setAttribute('aria-label', PANEL_NAMES[panel.id] || 'Dialog');
    }
  }

  function syncPanelTriggers() {
    document.querySelectorAll('a[data-panel]').forEach(function (a) {
      var panel = document.getElementById(a.dataset.panel);
      // aria-expanded is not allowed on an <a> without href (no link role)
      if (!panel || !a.hasAttribute('href')) { return; }
      // It opens a dialog in place (cart drawer, login, filters): a button
      a.setAttribute('role', 'button');
      a.setAttribute('aria-haspopup', 'dialog');
      a.setAttribute('aria-expanded', panel.getAttribute('aria-hidden') === 'false' ? 'true' : 'false');
    });
  }

  function onPanelChange(panel) {
    var open = panel.getAttribute('aria-hidden') === 'false';
    if (open && openPanelEl !== panel) {
      enhancePanel(panel);
      if (!openPanelEl) {
        var active = document.activeElement;
        panelOpener = active && active !== document.body && !panel.contains(active) ? active : panelOpener;
      }
      openPanelEl = panel;
      // Move focus into the dialog (the theme leaves it on the opener).
      // Wait a moment: the cart drawer re-renders its content after opening.
      setTimeout(function () {
        if (openPanelEl !== panel || panel.contains(document.activeElement)) { return; }
        var first = Array.prototype.find.call(panel.querySelectorAll(FOCUSABLE), function (el) {
          return isShown(el) && el.getAttribute('tabindex') !== '-1';
        });
        if (first) { first.focus(); }
      }, 400);
    } else if (!open && openPanelEl === panel) {
      openPanelEl = null;
      var opener = panelOpener;
      panelOpener = null;
      setTimeout(function () {
        if (openPanelEl) { return; }
        var a = document.activeElement;
        var target = returnTarget(opener);
        // The theme sends focus to the panel's icon (e.g. the cart) on close,
        // even when the user opened it from somewhere else (add to cart)
        var onPanelIcon = a && a.matches && a.matches('a[data-panel="' + panel.id + '"]');
        if (target && target !== a && (!a || a === document.body || panel.contains(a) || onPanelIcon)) { target.focus(); }
      }, 150);
    }
    syncPanelTriggers();
  }

  document.addEventListener('click', function (e) {
    // Only real clicks: after add to cart the theme opens the drawer by
    // clicking the cart icon itself, but the user was on the product
    if (!e.isTrusted) { return; }
    var trigger = e.target.closest && e.target.closest('a[data-panel], [data-quickshop]');
    if (trigger) { panelOpener = trigger; }
  }, true);

  // Where focus goes back to when a dialog closes. If the opener is hidden
  // again (e.g. a card's size buttons, shown only while the card has focus),
  // use the first visible control of its product card.
  function returnTarget(opener) {
    if (!opener || !document.contains(opener)) { return null; }
    if (isShown(opener)) { return opener; }
    var card = opener.closest('.product-card') || opener.closest('.l4cl > li') || opener.closest('li');
    return card ? Array.prototype.find.call(card.querySelectorAll('a[href]:not([tabindex="-1"]):not([aria-hidden="true"]), button:not([tabindex="-1"])'), isShown) || null : null;
  }

  // role="button": Space must not scroll the page (the theme opens on keyup)
  document.addEventListener('keydown', function (e) {
    if ((e.key === ' ' || e.key === 'Spacebar') && e.target.matches && e.target.matches('a[data-panel][role="button"]')) {
      e.preventDefault();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !openPanelEl || !isShown(openPanelEl) || upsellPopup()) { return; }
    var items = Array.prototype.filter.call(openPanelEl.querySelectorAll(FOCUSABLE + ', [role="button"]'), function (el) {
      return isShown(el) && el.getAttribute('tabindex') !== '-1';
    });
    if (!items.length) { return; }
    var first = items[0];
    var last = items[items.length - 1];
    if (!openPanelEl.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  function watchPanels(root) {
    (root || document).querySelectorAll('.m6pn:not([data-a11y-panel])').forEach(function (panel) {
      panel.dataset.a11yPanel = 'true';
      enhancePanel(panel);
      new MutationObserver(function () { onPanelChange(panel); })
        .observe(panel, { attributes: true, attributeFilter: ['aria-hidden'] });
    });
    syncPanelTriggers();
  }
  watchPanels();
  window.addEventListener('load', function () { watchPanels(); });

  /* ------------------------------------------------------------------------
     Checkout upsell popup (third-party app "lb-upsell", <lb-upsell-addon-popup>)
     Opens when Checkout is clicked. Its close icon, Add and Continue are
     plain <svg>/<div>s with click handlers, and focus stayed on Checkout.
     Add dialog semantics, keyboard buttons, move focus in, trap Tab, close
     on Escape, and return focus to the Checkout button. Look is unchanged.
     ------------------------------------------------------------------------ */
  var upsellOpener = null;
  var upsellWasOpen = false;

  function upsellPopup() {
    var box = document.getElementById('lb-addon-popup-container-id');
    return box && isShown(box) && window.getComputedStyle(box).display !== 'none' ? box : null;
  }

  function upsellButton(el, label) {
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    if (label) { el.setAttribute('aria-label', label); }
  }

  function enhanceUpsellPopup(box) {
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('tabindex', '-1');
    var title = box.querySelector('.lb-addon-popup-title');
    if (title && cleanText(title)) {
      title.id = title.id || 'a11y-upsell-title';
      box.setAttribute('aria-labelledby', title.id);
      box.removeAttribute('aria-label');
    } else {
      box.setAttribute('aria-label', 'Add-on offers');
    }
    var bg = document.getElementById('lb-addon-popup-bg-id');
    if (bg) { bg.setAttribute('aria-hidden', 'true'); }

    box.querySelectorAll('.lb-addon-popup-close-icon').forEach(function (svg) {
      // The app's X skips the offers and goes on to checkout
      upsellButton(svg, 'Close and continue to checkout');
      svg.setAttribute('focusable', 'true');
    });
    box.querySelectorAll('.lb-addon-popup-continue-btn').forEach(function (btn) { upsellButton(btn); });
    box.querySelectorAll('.lb-upsell-product-card').forEach(function (card) {
      var name = cleanText(card.querySelector('.lb-spc-details-name'));
      card.querySelectorAll('.lb-spc-details-btn').forEach(function (btn) {
        var text = (btn.textContent || '').replace(/\s+/g, ' ').trim();
        upsellButton(btn, name && text ? text + ' ' + name : '');
      });
      card.querySelectorAll('select').forEach(function (sel) {
        if (!sel.hasAttribute('aria-label')) { sel.setAttribute('aria-label', 'Size' + (name ? ' for ' + name : '')); }
      });
    });
  }

  function syncUpsellPopup() {
    var box = upsellPopup();
    if (box) {
      enhanceUpsellPopup(box);
      if (!upsellWasOpen) {
        var active = document.activeElement;
        upsellOpener = active && active !== document.body && !box.contains(active) ? active : null;
        if (upsellOpener) { upsellOpener.removeAttribute('aria-busy'); }
        liveRegion.textContent = '';
        box.focus({ preventScroll: true });
      }
    } else if (upsellWasOpen) {
      var opener = upsellOpener;
      upsellOpener = null;
      // Continue may already be on its way to checkout; only fix focus that was lost
      setTimeout(function () {
        var a = document.activeElement;
        if ((!a || a === document.body) && opener && document.contains(opener) && isShown(opener)) { opener.focus(); }
      }, 100);
    }
    upsellWasOpen = !!box;
  }

  var upsellQueued = false;
  new MutationObserver(function () {
    if (upsellQueued) { return; }
    upsellQueued = true;
    window.requestAnimationFrame(function () {
      upsellQueued = false;
      syncUpsellPopup();
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });

  // Capture phase: keep these keys away from the cart drawer and theme while the popup is open
  window.addEventListener('keydown', function (e) {
    var box = upsellPopup();
    if (!box) { return; }

    if (e.key === 'Escape' || e.key === 'Esc') {
      var close = box.querySelector('.lb-addon-popup-close-icon');
      if (close) {
        e.preventDefault();
        e.stopPropagation();
        close.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      }
      return;
    }

    if ((e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') && e.target.closest &&
        e.target.matches('[role="button"][tabindex="0"]') && box.contains(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      e.target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      return;
    }

    if (e.key !== 'Tab') { return; }
    var items = Array.prototype.filter.call(box.querySelectorAll(FOCUSABLE + ', [role="button"][tabindex="0"]'), function (el) {
      return isShown(el) && el.getAttribute('tabindex') !== '-1';
    });
    e.stopPropagation();
    if (!items.length) { e.preventDefault(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    var i = items.indexOf(document.activeElement);
    if (i === -1) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && i === 0) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && i === items.length - 1) {
      e.preventDefault();
      first.focus();
    }
  }, true);

  /* ------------------------------------------------------------------------
     Compact search overlay (header): toggle state, Escape to close,
     focus back to the toggle
     ------------------------------------------------------------------------ */
  var searchOpener = null;

  function searchToggles() {
    return document.querySelectorAll('.shopify-section-header a[aria-controls="search"]:not([aria-controls="nav"])');
  }

  function syncSearch() {
    var open = html.classList.contains('search-compact-active');
    searchToggles().forEach(function (a) {
      a.setAttribute('role', 'button');
      if (a.classList.contains('search-compact-toggle')) {
        a.setAttribute('aria-label', 'Close search');
      } else {
        a.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    });
    if (!open && searchOpener) {
      var opener = searchOpener;
      searchOpener = null;
      var search = document.getElementById('search');
      var a = document.activeElement;
      if (isShown(opener) && (!a || a === document.body || (search && search.contains(a)))) { opener.focus(); }
    }
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('.shopify-section-header a[aria-controls="search"]');
    if (t && !t.classList.contains('search-compact-toggle') && !html.classList.contains('search-compact-active')) { searchOpener = t; }
  }, true);

  document.addEventListener('keydown', function (e) {
    if ((e.key !== 'Escape' && e.key !== 'Esc') || !html.classList.contains('search-compact-active')) { return; }
    var search = document.getElementById('search');
    if (!search || !search.contains(document.activeElement)) { return; }
    var close = search.querySelector('.search-compact-toggle');
    var toggle = isShown(close) ? close : (searchOpener && isShown(searchOpener) ? searchOpener : Array.prototype.find.call(searchToggles(), isShown));
    if (toggle) {
      e.preventDefault();
      toggle.click();
    }
  });

  syncSearch();
  new MutationObserver(syncSearch).observe(html, { attributes: true, attributeFilter: ['class'] });

  /* ------------------------------------------------------------------------
     Live search suggestions (#livesearch, filled by custom-async.js
     liveSearch() while typing in #search_main; shown by .full on the form)
     - Escape closes the suggestions and keeps the text (a second Escape
       clears it, the browser's own behaviour for search fields)
     - Arrow Down/Up move between the input and the suggestion links
     - The number of suggestions is announced
     ------------------------------------------------------------------------ */
  var searchForm = document.getElementById('search');
  var searchInput = document.getElementById('search_main');
  var liveBox = document.getElementById('livesearch');
  var escClosedSearch = false;

  function suggestionsOpen() {
    return !!searchForm && searchForm.classList.contains('full') && isShown(liveBox);
  }

  function suggestionLinks() {
    return liveBox ? Array.prototype.filter.call(liveBox.querySelectorAll('a[href]'), isShown) : [];
  }

  // Same classes the theme removes when the field is emptied, minus the text ones
  function closeSuggestions() {
    html.classList.remove('search-full', 'search-full-mode', 'user-form-active');
    searchForm.classList.remove('full', 'processing');
  }

  if (searchForm && searchInput && liveBox) {
    liveBox.setAttribute('role', 'region');
    liveBox.setAttribute('aria-label', 'Search suggestions');

    searchForm.addEventListener('keydown', function (e) {
      var inList = liveBox.contains(e.target);
      if ((e.key === 'Escape' || e.key === 'Esc') && suggestionsOpen()) {
        e.preventDefault(); // keep the text: only close the list
        e.stopPropagation();
        closeSuggestions();
        escClosedSearch = true;
        searchInput.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') { return; }
      if (e.target !== searchInput && !inList) { return; }
      var links = suggestionLinks();
      if (!links.length || !suggestionsOpen()) { return; }
      var i = links.indexOf(e.target);
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        links[Math.min(i + 1, links.length - 1)].focus();
      } else if (i <= 0) {
        searchInput.focus();
      } else {
        links[i - 1].focus();
      }
    });

    // The theme re-opens the list on every keyup with text in the field
    window.addEventListener('keyup', function (e) {
      if (escClosedSearch && (e.key === 'Escape' || e.key === 'Esc')) {
        escClosedSearch = false;
        e.stopImmediatePropagation();
      }
    }, true);

    var announceTimer = null;
    new MutationObserver(function () {
      clearTimeout(announceTimer);
      announceTimer = setTimeout(function () {
        if (!suggestionsOpen() || searchForm.classList.contains('processing')) { return; }
        var n = liveBox.querySelectorAll('ul a[href]').length;
        window.a11yAnnounce(n ? n + (n === 1 ? ' search suggestion' : ' search suggestions') + ' available. Press arrow down to browse them.' : 'No search suggestions');
      }, 600);
    }).observe(liveBox, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Footer: landmarks, mobile accordion toggles (custom.js appends
     a.header-toggle "Close" to each heading and toggles .toggle on its parent)
     ------------------------------------------------------------------------ */
  function enhanceFooter() {
    var nav = document.getElementById('shopify-section-footer');
    if (nav) {
      nav.setAttribute('aria-label', 'Footer');
      // The theme renders the footer inside <main>; contentinfo must be top level
      var section = nav.closest('.shopify-section');
      if (section && !section.closest('main, [role="main"]')) { section.setAttribute('role', 'contentinfo'); }
      nav.querySelectorAll('a.header-toggle').forEach(function (t) {
        var heading = t.parentElement;
        var box = heading && heading.parentElement;
        var name = cleanText(heading).replace(/\s*Close$/, '');
        t.setAttribute('role', 'button');
        if (name) { t.setAttribute('aria-label', name + ' menu'); }
        if (box) { t.setAttribute('aria-expanded', box.classList.contains('toggle') ? 'true' : 'false'); }
        var list = box && box.querySelector(':scope > ul');
        if (list) {
          if (!list.id) { list.id = 'a11y-footer-list-' + Math.random().toString(36).slice(2, 8); }
          t.setAttribute('aria-controls', list.id);
        }
        if (!t.dataset.a11yBound) {
          t.dataset.a11yBound = 'true';
          t.addEventListener('click', function () {
            setTimeout(function () { t.setAttribute('aria-expanded', box.classList.contains('toggle') ? 'true' : 'false'); }, 0);
          });
          t.addEventListener('keydown', function (e) {
            if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); t.click(); }
          });
        }
      });
    }

    var cookie = document.getElementById('cookie-bar');
    if (cookie) {
      // It is a <footer>: "region" is not an allowed role there, "group" is
      cookie.setAttribute('role', 'group');
      cookie.setAttribute('aria-label', 'Cookie consent');
      cookie.querySelectorAll('.link-btn a').forEach(function (a) {
        a.setAttribute('role', 'button');
        if (a.dataset.a11yBound) { return; }
        a.dataset.a11yBound = 'true';
        a.addEventListener('keydown', function (e) {
          if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); a.click(); }
        });
      });
    }
  }
  enhanceFooter();
  window.addEventListener('load', enhanceFooter);

  /* ------------------------------------------------------------------------
     Theme alerts (custom.js "showAlert": add to cart, newsletter, forms)
     are injected silently. Read them out.
     ------------------------------------------------------------------------ */
  window.addEventListener('showAlert', function (e) {
    var d = e.detail || {};
    var header = d.header || '';
    var text = String(d.message || '').replace(/<[^>]*>/g, ' ');
    window.a11yAnnounce((header ? header + '. ' : '') + text.replace(/\s+/g, ' ').trim());
  });

  // Alerts already on the page after a reload (e.g. newsletter signup result)
  window.addEventListener('load', function () {
    setTimeout(function () {
      var alerts = document.querySelector('.l4al:not(.inline):not(.l4al-trustbadge)');
      if (alerts && cleanText(alerts)) { window.a11yAnnounce(cleanText(alerts).replace(/\s*Close\s*$/, '')); }
    }, 1000);
  });

  document.addEventListener('keydown', function (e) {
    if ((e.key === ' ' || e.key === 'Spacebar') && e.target.matches && e.target.matches('.l4al a.close')) {
      e.preventDefault();
      e.target.click();
    }
  });
  new MutationObserver(function () {
    document.querySelectorAll('.l4al a.close:not([role])').forEach(function (a) {
      a.setAttribute('role', 'button');
      a.setAttribute('aria-label', 'Close message');
    });
  }).observe(document.getElementById('root') || document.body, { childList: true, subtree: true });

  /* ------------------------------------------------------------------------
     Slider arrows: Swiper points aria-controls at its wrapper id, but some
     sliders (e.g. product recommendations, loaded later) end up pointing at
     an id that is not on the page. Re-point it, or drop it.
     ------------------------------------------------------------------------ */
  function fixSliderControls() {
    document.querySelectorAll('.swiper-button-nav[aria-controls], .swiper-pagination[aria-controls]').forEach(function (btn) {
      var id = btn.getAttribute('aria-controls');
      if (!id || document.getElementById(id)) { return; }
      var box = btn.closest('.swiper-custom-pagination');
      box = box ? box.parentElement : btn.parentElement;
      var wrapper = box && (box.querySelector('.swiper-wrapper') || box.closest('.swiper-wrapper'));
      if (!wrapper) {
        btn.removeAttribute('aria-controls');
      } else if (!wrapper.id) {
        wrapper.id = id;
      } else {
        btn.setAttribute('aria-controls', wrapper.id);
      }
    });
  }

  /* ------------------------------------------------------------------------
     Product group swatches (app, .fd-options-swatch__options): image-only
     links to sibling products with no name and no alt. Name each link after
     its product; the image inside is then decorative.
     ------------------------------------------------------------------------ */
  function productHandle(href) {
    var m = /\/products\/([^/?#]+)/.exec(href || '');
    return m ? m[1] : '';
  }

  var swatchTitles = {};

  function labelSwatch(a, name) {
    a.setAttribute('aria-label', name);
    if (a.classList.contains('active')) { a.setAttribute('aria-current', 'true'); }
    a.querySelectorAll('img:not([alt])').forEach(function (img) { img.setAttribute('alt', ''); });
  }

  function enhanceProductSwatches() {
    // Collection cards (.fd-options-swatch__options) and the product page use the same links
    var links = document.querySelectorAll('.fd-options-swatch__options a[href*="/products/"]:not([aria-label]), a.fd-flex[data-option-value][href*="/products/"]:not([aria-label])');
    if (!links.length) { return; }
    document.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var h = productHandle(a.getAttribute('href'));
      var t = visibleText(a);
      if (h && t && !swatchTitles[h]) { swatchTitles[h] = t; }
    });
    links.forEach(function (a) {
      if (a.dataset.a11ySwatch) { return; }
      // Product page swatches have a text ("Black"), hidden on mobile: keep it
      // as the name and make the image decorative
      var own = (a.textContent || '').replace(/\s+/g, ' ').trim();
      if (own) { a.dataset.a11ySwatch = 'true'; labelSwatch(a, own); return; }
      a.dataset.a11ySwatch = 'true';
      var h = productHandle(a.getAttribute('href'));
      if (!h) { return; }
      if (swatchTitles[h]) { labelSwatch(a, swatchTitles[h]); return; }
      // Not on the page (e.g. sibling colours on a product page): ask Shopify
      labelSwatch(a, h.replace(/-\d+$/, '').replace(/-/g, ' '));
      fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.title) { return; }
          swatchTitles[h] = p.title;
          document.querySelectorAll('a[data-a11y-swatch][href*="/products/' + h + '"]').forEach(function (x) { labelSwatch(x, p.title); });
        })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------------------------------
     Collection SEO text (.collection-bottom) starts with its own <h1>, but
     the page <h1> is the collection title. Shift those headings down one
     level for assistive tech only (tags stay, so styling and SEO stay).
     ------------------------------------------------------------------------ */
  function nestCollectionTextHeadings() {
    document.querySelectorAll('.collection-bottom :is(h1, h2, h3, h4, h5):not([aria-level])').forEach(function (h) {
      h.setAttribute('aria-level', String(parseInt(h.tagName.charAt(1), 10) + 1));
    });
  }

  /* ------------------------------------------------------------------------
     Text on slideshow slides and banner tiles sits on a colour that the
     theme paints with a ::before layer (or on the slide images), so the
     element itself has no background colour and contrast checkers measure
     the white text against the white page. Give the element the same
     colour that is already painted there (WCAG F24). The ::before still
     covers it exactly, so nothing changes visually; re-done on resize
     because radius and size change per breakpoint.
     ------------------------------------------------------------------------ */
  var MIRROR_BG = '.m6fr article.module-color-palette, .l4ft li > .main.module-color-palette, .m6bx.module-color-palette';
  // Buttons and the cart count badge paint their fill the same way. Hover
  // only changes the ::before colour (checked: size, radius and opacity stay),
  // so a resting colour underneath never shows. Only where the radius already
  // matches, so focus outlines keep their shape.
  var MIRROR_BG_CONTROLS = '.link-btn a, button, #cart-count';

  function paintedBefore(el) {
    var cs = window.getComputedStyle(el);
    var b = window.getComputedStyle(el, '::before');
    if (cs.backgroundColor !== 'rgba(0, 0, 0, 0)' || cs.position === 'static' || cs.zIndex === 'auto') { return null; }
    if (b.content === 'none' || b.position !== 'absolute' || b.opacity !== '1' || b.backgroundImage !== 'none' ||
        b.top !== '0px' || b.right !== '0px' || b.bottom !== '0px' || b.left !== '0px' ||
        !/^rgb\(/.test(b.backgroundColor)) { return null; }
    return { cs: cs, color: b.backgroundColor, radius: b.borderRadius };
  }

  function mirrorPaintedBackgrounds() {
    document.querySelectorAll('[data-a11y-bg]').forEach(function (el) {
      el.style.backgroundColor = '';
      if (el.dataset.a11yBg === 'radius') { el.style.borderRadius = ''; }
      el.removeAttribute('data-a11y-bg');
    });

    document.querySelectorAll(MIRROR_BG).forEach(function (el) {
      var p = paintedBefore(el);
      if (!p) { return; }
      var setRadius = false;
      if (p.cs.borderRadius !== p.radius) {
        // Rounding our own background only matches if nothing visible is clipped by it
        var media = el.querySelector(':scope > figure');
        if (p.cs.overflow !== 'visible' && !(media && window.getComputedStyle(media).borderRadius === p.radius)) { return; }
        setRadius = true;
      }
      el.style.backgroundColor = p.color;
      if (setRadius) { el.style.borderRadius = p.radius; }
      el.dataset.a11yBg = setRadius ? 'radius' : 'color';
    });

    document.querySelectorAll(MIRROR_BG_CONTROLS).forEach(function (el) {
      var p = paintedBefore(el);
      if (!p || p.cs.borderRadius !== p.radius) { return; }
      el.style.backgroundColor = p.color;
      el.dataset.a11yBg = 'color';
    });

    // Slideshow dots and play/pause float over the slides, outside the slide
    // article. A sliding (not fading) slideshow always covers its frame.
    document.querySelectorAll('.m6fr').forEach(function (frame) {
      var outer = frame.querySelector(':scope > .swiper-outer');
      var slide = frame.querySelector('article.module-color-palette');
      if (!outer || !slide || outer.classList.contains('swiper-fade') || slide.dataset.a11yBg !== 'color') { return; }
      var cs = window.getComputedStyle(frame);
      var a = frame.getBoundingClientRect();
      var b = outer.getBoundingClientRect();
      if (cs.overflow !== 'hidden' || cs.backgroundColor !== 'rgba(0, 0, 0, 0)' || cs.zIndex === 'auto' ||
          Math.abs(a.width - b.width) > 1 || Math.abs(a.height - b.height) > 1) { return; }
      frame.style.backgroundColor = slide.style.backgroundColor;
      frame.dataset.a11yBg = 'color';
    });
  }

  /* ------------------------------------------------------------------------
     Lists that scroll sideways on mobile (.mobile-compact / .mobile-scroll)
     but hold nothing focusable (e.g. location tiles) can't be scrolled with
     the keyboard. Make the list itself focusable, named after its heading.
     ------------------------------------------------------------------------ */
  function enhanceScrollLists() {
    document.querySelectorAll('ul.mobile-compact, ul.mobile-scroll').forEach(function (ul) {
      var scrolls = ul.scrollWidth > ul.clientWidth + 1 && /(auto|scroll)/.test(window.getComputedStyle(ul).overflowX);
      var focusable = ul.querySelector(FOCUSABLE);
      if (scrolls && !focusable) {
        if (!ul.hasAttribute('tabindex')) {
          ul.setAttribute('tabindex', '0');
          ul.dataset.a11yScroll = 'true';
        }
        if (!ul.hasAttribute('aria-label')) {
          var box = ul.closest('.shopify-section');
          var heading = box && box.querySelector('h1, h2, h3');
          ul.setAttribute('aria-label', cleanText(heading) || 'Scrollable list');
        }
      } else if (ul.dataset.a11yScroll) {
        ul.removeAttribute('tabindex');
        ul.removeAttribute('data-a11y-scroll');
      }
    });
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      mirrorPaintedBackgrounds();
      enhanceScrollLists();
    }, 250);
  });

  /* ------------------------------------------------------------------------
     Product card prices (snippets/product-item.liquid): hidden "Regular price"
     / "Sale price" labels. The theme rewrites the price on variant change
     (custom-async.js: .price.innerHTML = ...), which drops them: put them back.
     ------------------------------------------------------------------------ */
  function priceLabel(text) {
    var span = document.createElement('span');
    span.className = 'a11y-sr-only a11y-price-label';
    span.textContent = text + ' ';
    return span;
  }

  function labelCardPrices() {
    document.querySelectorAll('.product-card p.price, .l4cl li p.price').forEach(function (p) {
      if (p.querySelector('.a11y-price-label')) { return; }
      var old = p.querySelector('.old-price');
      var onSale = old && !old.classList.contains('hidden') && old.textContent.trim();
      if (p.querySelector('.price-varies')) {
        p.insertBefore(priceLabel('Price range'), p.firstChild);
      } else if (onSale) {
        old.insertBefore(priceLabel('Regular price'), old.firstChild);
        old.parentNode.insertBefore(priceLabel('Sale price'), old.nextSibling);
      } else {
        p.insertBefore(priceLabel('Regular price'), old ? old.nextSibling : p.firstChild);
      }
    });
  }

  /* ------------------------------------------------------------------------
     Section wrappers are <article> elements, read as an unnamed "article".
     Name each one after its heading (aria-labelledby):
     1. its own visible heading (an <h1> first: product page), not the
        headings of cards inside it or of the video transcript panel
     2. else the title of a title-only section just before it (e.g. the
        "locations" title above the location cards, an event title above its
        photos/video), stepping back over other unnamed sections of the group
     3. else, if it holds nothing to read or use, role="none"
     ------------------------------------------------------------------------ */
  var articleHeadingId = 0;
  var HEADINGS = 'h1, h2, h3, h4, h5, h6';

  function usableHeading(h, box) {
    if (!h || !isShown(h) || h.closest('.vab-transcript, [hidden], [aria-hidden="true"]')) { return false; }
    var card = h.closest('li');
    return !(card && box.contains(card));
  }

  function ownHeading(box) {
    var h1 = Array.prototype.find.call(box.querySelectorAll('h1'), function (h) { return usableHeading(h, box); });
    return h1 || Array.prototype.find.call(box.querySelectorAll(HEADINGS), function (h) { return usableHeading(h, box); });
  }

  // A text-only intro section (rich text: a heading, maybe a paragraph, no media/links)
  // titles the sections after it
  function titleOnlyHeading(section) {
    var h = ownHeading(section);
    return h && !section.querySelector('img, video, iframe, a[href], button, input, li') ? h : null;
  }

  function labelArticles() {
    // Articles marked decorative earlier are checked again: some fill in later (recommendations)
    document.querySelectorAll('article:not([aria-label]):not([aria-labelledby]):not([role]), article[data-a11y-empty]').forEach(function (a) {
      var empty = !a.textContent.trim() && !a.querySelector('img[alt]:not([alt=""]), video, iframe, a[href], button, input, select, textarea');
      if (a.hasAttribute('data-a11y-empty')) {
        if (empty) { return; }
        a.removeAttribute('role');
        a.removeAttribute('data-a11y-empty');
      }
      var h = ownHeading(a);
      if (!h) {
        var section = a.closest('.shopify-section');
        var prev = section && section.previousElementSibling;
        for (var i = 0; prev && i < 3 && !h; i++, prev = prev.previousElementSibling) {
          h = titleOnlyHeading(prev);
          if (!h && ownHeading(prev)) { break; } // another group starts here
        }
      }
      if (h) {
        if (!h.id) { h.id = 'a11y-article-title-' + (++articleHeadingId); }
        a.setAttribute('aria-labelledby', h.id);
      } else if (empty) {
        a.setAttribute('role', 'none');
        a.setAttribute('data-a11y-empty', '');
      }
    });
  }

  /* ------------------------------------------------------------------------
     Cart / product quantity +/- (custom-async.js amountRun: "Increase by 1",
     misspelt "Descrease by 1"): name them after the product of their input
     ------------------------------------------------------------------------ */
  function labelQuantityButtons() {
    document.querySelectorAll('a.incr, a.decr').forEach(function (b) {
      var input = b.parentElement && b.parentElement.querySelector('input');
      var label = input && (input.getAttribute('aria-label') || '');
      var what = /^Quantity for /.test(label) ? label.replace(/^Quantity for /, '') : '';
      var up = b.classList.contains('incr');
      b.setAttribute('aria-label', (up ? 'Increase quantity' : 'Decrease quantity') + (what ? ' of ' + what : ''));
    });
  }

  /* ------------------------------------------------------------------------
     Phone numbers written as plain text (footer, menu, page content):
     make them tap-to-call links that look the same (class a11y-tel)
     ------------------------------------------------------------------------ */
  var PHONE = /(\+?1[\s.-]?)?\(?\b(\d{3})\)?[\s.-]?(\d{3})[\s.-](\d{4})\b/;

  function linkPhoneNumbers() {
    document.querySelectorAll('footer, #nav, .shopify-section-footer, main .rte, main [class*="rich-text"], main [id*="rich_text"], main .m6cn, main .m6as, main .l4ft').forEach(function (root) {
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          if (!PHONE.test(n.nodeValue)) { return NodeFilter.FILTER_REJECT; }
          return n.parentElement.closest('a, button, script, style, textarea, input, select, [contenteditable], .a11y-tel') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        }
      });
      var nodes = [];
      while (walker.nextNode()) { nodes.push(walker.currentNode); }
      nodes.forEach(function (n) {
        var m = PHONE.exec(n.nodeValue);
        if (!m) { return; }
        var start = n.nodeValue.indexOf(m[0]);
        var match = n.splitText(start);
        match.splitText(m[0].length);
        var a = document.createElement('a');
        a.className = 'a11y-tel';
        a.href = 'tel:+1' + m[2] + m[3] + m[4];
        a.textContent = m[0];
        // built from the digits: page text may split "(" off into another element
        a.setAttribute('aria-label', 'Phone Number: (' + m[2] + ') ' + m[3] + '-' + m[4]);
        a.dataset.a11ySpecial = 'true';
        match.parentNode.replaceChild(a, match);
      });
    });
  }

  /* Breadcrumbs: mark the current page (last item) */
  function markBreadcrumbCurrent() {
    document.querySelectorAll('.n6br ol').forEach(function (ol) {
      var last = ol.querySelector(':scope > li:last-child');
      if (!last || last.querySelector('[aria-current]')) { return; }
      var link = last.querySelector('a');
      if (link) { link.setAttribute('aria-current', 'page'); return; }
      var span = document.createElement('span');
      span.setAttribute('aria-current', 'page');
      while (last.firstChild) { span.appendChild(last.firstChild); }
      last.appendChild(span);
    });
  }

  /* Star ratings ("4.40 stars"): "Rated 4.4 out of 5 stars, 12 reviews" */
  function labelRatings() {
    document.querySelectorAll('[role="img"][aria-label$="stars"]:not([data-a11y-rating]), .r6rt[data-val]:not([data-a11y-rating])').forEach(function (r) {
      r.dataset.a11yRating = 'true';
      var val = parseFloat(r.getAttribute('data-val') || (r.getAttribute('aria-label') || '').replace(/[^\d.]/g, ''));
      if (!isFinite(val)) { return; }
      var of = parseFloat(r.getAttribute('data-of')) || 5;
      // Review count sits next to the stars (Judge.me badge text "15 reviews" / "(15)")
      var box = r.closest('.jdgm-prev-badge, .r6rt, .jdgm-widget') || r.parentElement;
      var m = (box ? box.textContent : '').match(/\((\d+)\)|(\d+)\s*reviews?/i) || [];
      var count = m[1] || m[2];
      var label = 'Rated ' + (Math.round(val * 10) / 10) + ' out of ' + of + ' stars' + (count ? ', ' + count + ' reviews' : '');
      r.setAttribute('role', 'img');
      r.setAttribute('aria-label', label);
    });
  }

  /* Header search: combobox state for the suggestions panel */
  function syncSearchCombobox() {
    var input = document.getElementById('search_main');
    var form = document.getElementById('search');
    if (!input || !form || !document.getElementById('livesearch')) { return; }
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-controls', 'livesearch');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', form.classList.contains('full') ? 'true' : 'false');
  }
  if (document.getElementById('search')) {
    new MutationObserver(syncSearchCombobox).observe(document.getElementById('search'), { attributes: true, attributeFilter: ['class'] });
  }

  /* Pressed state for theme toggles: collection grid view, password show/hide */
  document.addEventListener('click', function (e) {
    var grid = e.target.closest && e.target.closest('a[aria-pressed][aria-label$="product images"]');
    if (grid) {
      grid.closest('ul').querySelectorAll('a[aria-pressed]').forEach(function (g) { g.setAttribute('aria-pressed', g === grid ? 'true' : 'false'); });
    }
    var show = e.target.closest && e.target.closest('label a.show');
    if (show) {
      setTimeout(function () {
        var input = document.getElementById(show.closest('label').htmlFor);
        var shown = input && input.type === 'text';
        show.setAttribute('aria-pressed', shown ? 'true' : 'false');
        show.setAttribute('aria-label', shown ? 'Hide password' : 'Show password');
      }, 0);
    }
  });

  /* Script-only links (href="./" with role=button, panels, toggles): never
     navigate, even if pressed before the theme's script is ready */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href="./"]');
    if (a && a.matches('[role="button"], [data-panel], [aria-controls], .toggle, .toggle-back, .close, .close-nav, .header-toggle, .show, .link-more, .clear-toggle')) {
      e.preventDefault();
    }
  }, true);

  /* Checkout buttons: say what is happening after the click */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('button[name="checkout"]');
    if (!b) { return; }
    b.setAttribute('aria-busy', 'true');
    window.a11yAnnounce('Processing, going to checkout.');
  });

  // Content above is built or replaced after load (apps, sliders, sections)
  var refreshQueued = false;
  function refreshDynamic() {
    refreshQueued = false;
    fixSliderControls();
    enhanceProductSwatches();
    nestCollectionTextHeadings();
    enhanceGenericLinks();
    enhanceSpecialLinks();
    labelCardPrices();
    labelArticles();
    labelQuantityButtons();
    // Option lists made radiogroups (main-product.liquid): items are not list items any more
    document.querySelectorAll('ul[role="radiogroup"] > li:not([role])').forEach(function (li) { li.setAttribute('role', 'none'); });
    linkPhoneNumbers();
    markBreadcrumbCurrent();
    labelRatings();
    syncSearchCombobox();
    // plugin-selects.js adds a search field to every custom dropdown, unlabelled
    document.querySelectorAll('input.bv_input:not([aria-label])').forEach(function (i) {
      i.setAttribute('aria-label', 'Search options');
    });
  }
  function queueRefresh() {
    if (refreshQueued) { return; }
    refreshQueued = true;
    setTimeout(refreshDynamic, 200);
  }
  refreshDynamic();
  window.addEventListener('load', function () {
    refreshDynamic();
    mirrorPaintedBackgrounds();
    enhanceScrollLists();
    // Swiper builds the slideshow a little after load
    setTimeout(mirrorPaintedBackgrounds, 2000);
  });
  new MutationObserver(queueRefresh).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-controls'] });

  enhanceVariantPickers();
  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.type === 'attributes') {
        if (m.target.matches('ul.variant-picker label')) { syncSoldOut(m.target); }
      } else {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType === 1 && n.querySelector) { enhanceVariantPickers(n); }
        });
      }
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
})();
